import { ApolloLink, NextLink, Operation } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition, removeArgumentsFromDocument } from '@apollo/client/utilities';
import { isNil } from 'lodash';
import {
  graphqlErrorFingerprint,
  operationNameOrFallback,
} from '@shared/api/graphqlErrorFingerprint';
import possibleTypes from '@shared/api/possibleTypes.json';
import { MBAuthUtil } from '@shared/scenes/auth/util';
import { getStore } from '@shared/store';
import { getCurrentBusinessId } from '@shared/store/appState/AuthTokenManager';
import { omitTypeName } from '@shared/util/omitTypeName';
import { MBSentry } from '@shared/util/sentry';
import { parseBusinessIdFromToken } from '@shared/util/token';

const ALLOWED_UNAUTHENTICATED_MUTATIONS = [
  'AcceptBusinessInviteMutation',
  'RequestAuthCodeMutation',
  'VerifyAuthCodeMutation',
];

/**
 * Org login issues a business JWT only (no user session). That JWT must not be sent as
 * `Authorization: Bearer` for general queries — backends reject them as invalid user JWTs.
 * Only these operations are allowed to use the biz token as Bearer (see `myBusiness`, etc.).
 */
const OPERATIONS_ALLOWING_BIZ_JWT_BEARER = new Set(['GetBusinessLoginPeopleQuery']);

function shouldUseBizJwtAsBearer(operation: Operation): boolean {
  const name = operation.operationName;
  if (!name) {
    return false;
  }
  return OPERATIONS_ALLOWING_BIZ_JWT_BEARER.has(name);
}

function getAuthorizationHeader(operation: Operation): string | undefined {
  const authToken = MBAuthUtil.getAuthToken();
  const bizAuthToken = MBAuthUtil.getBizAuthToken();
  if (!!authToken) {
    return `Bearer ${authToken}`;
  }
  if (!!bizAuthToken && shouldUseBizJwtAsBearer(operation)) {
    return `Bearer ${bizAuthToken}`;
  }
  return undefined;
}

// Log all GraphQL errors to sentry
// https://www.npmjs.com/package/apollo-link-error
export const errorLoggerLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (!!networkError) {
    MBSentry.breadcrumb({
      category: 'apollo',
      message: networkError.message,
      data: {
        operation: operation.operationName,
        variables: JSON.stringify(operation.variables),
      },
    });
  }
  if (!!graphQLErrors) {
    graphQLErrors.forEach(error => {
      MBSentry.breadcrumb({
        category: 'apollo',
        message: error.message,
        data: {
          operation: operation.operationName,
          variables: JSON.stringify(operation.variables),
        },
      });
      const operationName = operationNameOrFallback(operation.operationName);
      const reportedError = new Error(error.message);
      // All GraphQL errors are captured from this one spot, so use a distinct
      // fingerprint for better aggregation in Sentry.
      reportedError.name = `GraphQLError: ${operationName}`;
      MBSentry.exception({
        error: reportedError,
        data: {
          operation: operationName,
          variables: JSON.stringify(operation.variables),
        },
        fingerprint: graphqlErrorFingerprint(operation.operationName, error),
        tags: { graphqlOperation: operationName },
      });
    });
  }
});

export function customRequestHeadersLink(
  headers: () => Promise<Record<string, string>>,
): ApolloLink {
  return setContext(async (_request, previousContext) => {
    previousContext.headers = {
      ...previousContext.headers,
      ...(await headers()),
    };

    return previousContext;
  });
}

function xCoastBusinessIdHeader(): { 'x-coast-business-id': string } | undefined {
  const store = getStore();
  const authToken = MBAuthUtil.getAuthToken();
  const bizAuthToken = MBAuthUtil.getBizAuthToken();
  const fromStateOrStorage = store.getState().appState.currentBusinessId ?? getCurrentBusinessId();
  // Org-login: JWT is source of truth; `myBusiness` has no field args — header must match token.
  if (!authToken && !!bizAuthToken) {
    const fromJwt = parseBusinessIdFromToken(bizAuthToken);
    if (!isNil(fromJwt)) {
      return { 'x-coast-business-id': String(fromJwt) };
    }
  }
  if (!isNil(fromStateOrStorage)) {
    return { 'x-coast-business-id': String(fromStateOrStorage) };
  }
  return undefined;
}

export function authLink(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    const context = operation.getContext();
    const authorization = getAuthorizationHeader(operation);
    const businessIdHeader = xCoastBusinessIdHeader();
    context.headers = {
      ...context.headers,
      // Note(fant): for legacy reasons, this is "WEB" | "IOS" | "ANDROID"
      'x-ts-platform': 'WEB',
      'x-coast-client-version': import.meta.env.VITE_VERSION_STRING,
      authorization,
      ...businessIdHeader,
    };
    if (isNil(context.headers.authorization) && !isAllowedUnauthenticatedMutation(operation)) {
      return null;
    }
    operation.setContext(context);
    return forward(operation);
  });
}

export const removeTypenameFromMutationLink = new ApolloLink(
  (operation: Operation, forward: NextLink) => {
    if (isMutation(operation)) {
      operation.variables = omitTypeName(operation.variables);
      return forward(operation);
    }
    return forward(operation);
  },
);

// We have to remove dynamic @connection keys in order to not confuse the server.
// Then they have to be re-added in the keyArgs func.
// See listUsers for example.
// This assumes any dynamic @connection key is called "customCacheKey".
// It's still possible to hardcode any @connection key in the query.
// See messages for an example of this.
// https://github.com/apollographql/apollo-client/issues/11099
// https://community.apollographql.com/t/multiple-lists-w-same-query/6663/6
export const removeCustomCacheKeyLink = new ApolloLink(
  (operation: Operation, forward: NextLink) => {
    const queryWithRemovedCustomCacheKey = removeArgumentsFromDocument(
      [{ name: 'customCacheKey' }],
      operation.query,
    );
    operation.query = !isNil(queryWithRemovedCustomCacheKey)
      ? queryWithRemovedCustomCacheKey
      : operation.query;

    return forward(operation);
  },
);

// Stamp __cacheKeyWorkflowTemplateId onto component objects in responses before they reach the cache,
// so composite keyFields ['__cacheKeyWorkflowTemplateId', 'id'] can be computed during normalization.
// This is stored separately from the workflowTemplateId field so that components like RelatedCardComponent
// can have workflowTemplateId as actual data (which can be null) while still being cacheable by parent workflow.
const COMPONENT_TYPENAMES = new Set<string>(possibleTypes.AnyComponent);

export const stampWorkflowTemplateIdLink = new ApolloLink(
  (operation: Operation, forward: NextLink) => {
    return forward(operation).map(result => {
      if (!result?.data) {
        return result;
      }
      try {
        annotateWithWorkflowTemplateId(result.data, undefined);
      } catch (_e) {
        // best-effort only
      }
      return result;
    });
  },
);

function annotateWithWorkflowTemplateId(node: any, currentWorkflowTemplateId?: string) {
  if (!node || typeof node !== 'object') {
    return;
  }

  const typename = (node as any).__typename as string | undefined;
  let nextWorkflowTemplateId = currentWorkflowTemplateId;

  if (typename === 'WorkflowTemplate' && typeof (node as any).id === 'string') {
    nextWorkflowTemplateId = (node as any).id as string;
  }

  if (nextWorkflowTemplateId && typename && COMPONENT_TYPENAMES.has(typename)) {
    try {
      Object.defineProperty(node, '__cacheKeyWorkflowTemplateId', {
        value: nextWorkflowTemplateId,
        enumerable: false,
        writable: false,
      });
    } catch (_e) {
      // ignore
    }
  }

  for (const key of Object.keys(node)) {
    const value = (node as any)[key];
    if (Array.isArray(value)) {
      for (const child of value) {
        annotateWithWorkflowTemplateId(child, nextWorkflowTemplateId);
      }
    } else if (value && typeof value === 'object') {
      annotateWithWorkflowTemplateId(value, nextWorkflowTemplateId);
    }
  }
}

function isAllowedUnauthenticatedMutation(operation: Operation) {
  return (
    isMutation(operation) && ALLOWED_UNAUTHENTICATED_MUTATIONS.includes(operation.operationName)
  );
}

function isMutation(operation: Operation): boolean {
  const definition = getMainDefinition(operation.query);
  return definition?.kind === 'OperationDefinition' && definition?.operation === 'mutation';
}
